<template>
    <section>
        <div class="desc">
            <img :src="CoWarning" alt="warning icon"/>
            <p v-html="$t('my_sim.country_limit_warning.desc')"></p>
        </div>
        <div class="solution" v-for="(solution, index) in solutions" :key="solution.texts[0] ?? index">
            <p v-for="(text, i) in solution.texts" :key="text + i" v-html="text"></p>
            <div class="action">
                <template v-if="solution.actionStatusText">
                    <reo-processing v-if="solution.actionStatusText.type === 'loading'" loading-class="loading-icon" custom-class="loading-wrapper"/>
                    <p :class="solution.actionStatusText.type">
                        {{ solution.actionStatusText.text }}
                    </p>
                </template>
                <reo-common-button
                    :label="solution.actionText"
                    @click="solution.action"
                    size="small"
                    v-else
                />
                
            </div>
        </div>
        <p class="note" v-html="$t('my_sim.country_limit_warning.note')"></p>
    </section>
</template>

<script lang="ts">
import Vue from 'vue';
import { SimManager } from '@/lib/simManager';
import CoWarning from '@/common/images/common/combo/co_warning.svg';
import { activateSimCard } from '@/api/sim';
import { getErrorSymbol } from '@/common/js/utils';
import { unfoldError } from '@/common/js/notice';
import dayjs from 'dayjs';
import ReoProcessing from '@/basic/processing.vue';
import * as _ from '@reolink-web/utils';

const WAITING_END_TIME_KEY = 'country_limit_waiting_end_time';

interface ISolutionItem {
    texts: string[];
    action: () => unknown;
    actionText: string;
    actionStatusText?: {
        text: string;
        type: 'loading' | 'info';
    };
}

export default Vue.extend({
    name: 'CountryLimitCard',
    components: {
        ReoProcessing,
    },
    props: {
        simManager: {
            type: SimManager,
            required: true,
        },
    },
    data() {

        return {
            CoWarning,
            activateStatus: null as null | 'loading' | 'info',
            waitTimeText: '',
            timer: null as null | NodeJS.Timeout,
        };
    
    },
    computed: {
        waitingEndTimeKey(): string {

            return `${WAITING_END_TIME_KEY}-${ this.simManager.iccid }`;
        
        },
        solutions(): ISolutionItem[] {

            return [
                {
                    texts: [
                        this.$t( 'my_sim.country_limit_warning.solution1.0' ).toString(),
                        this.$t( 'my_sim.country_limit_warning.solution1.1', {
                            country: this.simManager.lastUsedCountry
                        }).toString(),
                    ],
                    action: () => {

                        this.$emit( 'switchPlan' );
                    
                    },
                    actionText: this.$t( 'dashboard.device_detail.more_menu.switch' ).toString(),

                },
                {
                    texts: [
                        this.$t( 'my_sim.country_limit_warning.solution2.0' ).toString(),
                    ],
                    action: () => {

                        this.handleActivateSim().catch( console.error );
                    
                    },
                    actionText: this.$t( 'my_sim.country_limit_warning.active' ).toString(),
                    actionStatusText: this.activateStatus ? {
                        text: this.activateStatus === 'loading'
                            ? this.$t( 'my_sim.country_limit_warning.activating' ).toString()
                            : this.$t( 'my_sim.country_limit_warning.waiting_time', {
                                time: this.waitTimeText
                            }).toString(),
                        type: this.activateStatus,
                    } : undefined
                },
            ];
        
        }
    },
    created() {

        if ( this.simManager.trafficLimitStatus === 'recovering' ) {

            this.activateStatus = 'loading';
            this.handleRefreshInRecovering();
        
        }
        this.secondTimer();
    
    },
    beforeDestroy() {

        if ( this.timer ) {

            clearTimeout( this.timer );
            this.timer = null;

        }
    
    },
    methods: {
        async handleActivateSim() {

            try {

                await activateSimCard( this.simManager.iccid );
                this.activateStatus = 'loading';
                this.$emit( 'refresh' );
                this.handleRefreshInRecovering();
            
            } catch ( e ) {

                console.error( e );
                const symbol = getErrorSymbol( e );
                if ( ['activate_request_throttled', 'request_throttled'].includes( symbol )) {

                    this.activateStatus = 'info';
                    this.waitTimeText = '60m 0s';
                    localStorage.setItem( this.waitingEndTimeKey, dayjs().add( 1, 'hour' ).valueOf().toString());
                    this.secondTimer();
                
                } else {

                    unfoldError( 'cart.errors.order_common_error_message' );
                
                }

            }
        
        },
        secondTimer() {

            const current = dayjs();
            const endTime = dayjs( Number( localStorage.getItem( this.waitingEndTimeKey ) ?? 0 ));
            if ( current.isAfter( endTime )) {

                this.activateStatus = null;
                this.waitTimeText = '';
                
            } else {

                this.activateStatus = 'info';
                const diffMinute = endTime.diff( current, 'minute' );
                const diffSecond = endTime.diff( current, 'second' ) % 60;
                this.waitTimeText = `${ diffMinute }m ${ diffSecond }s`;
                this.timer = setTimeout(() => {

                    this.secondTimer();

                }, 1000 );
                
            }
                 
        },
        handleRefreshInRecovering() {

            setTimeout(() => {

                if ( this.simManager.trafficLimitStatus === 'recovering' ) {

                    this.$emit( 'refresh' );
                    this.handleRefreshInRecovering();
                
                }
            
            }, _.DateTime.MS_PER_MINUTE );
        
        }
        
    }
});
</script>

<style lang="less" scoped>
section{
    background: linear-gradient(0deg, rgba(250, 149, 80, 0.2), rgba(250, 149, 80, 0.2)), #FFFFFF;
    border-radius: 12px;
    padding: 24px 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    .desc{
        display: flex;
        gap: 8px;
        align-items: flex-start;
        margin-bottom: 4px;
        p{
            font-size: 14px;
            line-height: 20px;
            font-weight: normal;
        }
        img{
            width: 24px;
            height: 24px;
        }
    }
    .solution{
        background-color: @bg_03;
        border-radius: 8px;
        padding: 20px;
        gap: 16px;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        font-size: 14px;
        .action{
            display: flex;
            gap: 8px;
            align-items: center;
            p.info{
                color: @orange_6;
            }
            p.loading{
                color: @green_6;
                line-height: 20px;
            }
            /deep/ .loading-icon{
                width: 20px;
                height: 20px;
                padding: 0;
                background-color: @bg_03;
                color: @green_6;
            }
            /deep/ .loading-wrapper{
                min-height: 0;
                height: 20px;
            }
        }
    }
    .note{
        font-size: 12px;
        line-height: 18px;
        padding: 0 16px;
    }
}
</style>
